import React, { useRef, useState } from "react";

// components
import SideViewModal from "../../../../../components/modals/SideViewModal/SideViewModal.component";
import EditJobDay from "../../EditJobDay.form";

// queries
import { useMutation } from "@apollo/client";
import { EDIT_JOB_DAY } from "../../../../../graphql/jobDay/jobDay.mutations";
import {
  GET_JOB_DAY_BY_ID_FOR_EDIT_FORM,
  GET_JOB_DAY_BY_ID_WITH_JOB_INFO,
  GET_JOB_DAY_WITH_NAME_DATE_AND_STATUS,
  GET_JOB_DAY_CREW_ASSIGNMENTS,
} from "../../../../../graphql/jobDay/jobDay.queries";
import { apolloClient } from "../../../../../index";

// utils
import { setupJobDayPrefillData } from "./EditJobDay.form.controller.utils";
import { validate } from "../../../../../shared/utility";
import moment from "moment";

// MUI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";

const validationSchema = {
  job: { type: "input", validation: { required: true } },
  scopeOfWork: { type: "input", validation: {} },
  date: { type: "date", validation: { required: true, isDate: true } },
  startTime: { type: "time", validation: { required: true, isTime: true } },
  endTime: { type: "time", validation: { isTime: true } },
  vehicleOption: { type: "input", validation: {} }, // ADD VALIDATION
};

const toInt = (v) => {
  const n = Number.parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
};

function extractApolloError(err) {
  const msgs = [];
  if (err?.graphQLErrors?.length) {
    for (const ge of err.graphQLErrors) {
      if (ge?.message) msgs.push(ge.message);
      const det = ge?.extensions?.exception?.detail || ge?.extensions?.detail;
      if (det) msgs.push(det);
      const fieldErrors = ge?.extensions?.exception?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        for (const [f, arr] of Object.entries(fieldErrors)) {
          msgs.push(`${f}: ${Array.isArray(arr) ? arr.join(", ") : String(arr)}`);
        }
      }
    }
  }
  if (err?.networkError?.result?.errors?.length) {
    for (const e of err.networkError.result.errors) if (e?.message) msgs.push(e.message);
  }
  if (!msgs.length && err?.networkError?.message) msgs.push(err.networkError.message);
  if (!msgs.length && err?.message) msgs.push(err.message);
  return msgs.join("\n");
}

export default function EditJobDayFormController({ id, visible, closeForm }) {
  const [isFetchingObject, setIsFetchingObject] = useState(true);
  const [jobDay, setJobDay] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // dialogs
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // state to keep pending formData if confirmation needed
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const submittingRef = useRef(false);
  const [editJobDay] = useMutation(EDIT_JOB_DAY);

  const doSubmit = (submitData, idInt) => {
    editJobDay({
      variables: {
        ...submitData,
        id: idInt,
        crewAssignmentsToDelete: submitData.crewAssignmentsToDelete.map((v) => toInt(v)),
      },
      refetchQueries: [
        { query: GET_JOB_DAY_BY_ID_WITH_JOB_INFO, variables: { id: idInt } },
        { query: GET_JOB_DAY_WITH_NAME_DATE_AND_STATUS, variables: { id: idInt } },
        { query: GET_JOB_DAY_CREW_ASSIGNMENTS, variables: { id: idInt } },
      ],
      awaitRefetchQueries: true,
    })
      .then((result) => {
        if (result?.data?.editJobDay) {
          setErrors({});
          setIsFetchingObject(true);
          closeForm();
        } else {
          setErrorText("Update failed. Please verify your inputs and try again.");
          setErrorOpen(true);
        }
      })
      .catch((e) => {
        const msg = extractApolloError(e) || "Something went wrong.";
        console.error(e);
        setErrorText(msg);
        setErrorOpen(true);
      })
      .finally(() => {
        setIsSubmitting(false);
        submittingRef.current = false;
        setPendingSubmit(null);
      });
  };

  const handleSubmit = (formData) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    const newCrewAssignments = [];
    (formData.crewAssignments || []).forEach((assignment) => {
      if (assignment.id === 0) {
        const cmId = toInt(assignment.crewMember);
        if (cmId != null) newCrewAssignments.push(cmId);
      }
    });

    const { isValid, errors: vErrors } = validate(formData, validationSchema);
    if (!isValid) {
      setErrors(vErrors);
      setIsSubmitting(false);
      submittingRef.current = false;
      return;
    }

    const rawDeletionList = formData.crewMemberDelete ?? formData.deleteAssignments ?? [];
    const normalizedDeletionIds = (Array.isArray(rawDeletionList) ? rawDeletionList : [])
      .map((v) => toInt(v))
      .filter((v) => v != null);

    // Handle vehicleOption - send null if empty string
    const vehicleOption = formData.vehicleOption || null;

    const submitData = {
      job: toInt(formData.job),
      truck: toInt(formData.truck),
      driver: toInt(formData.driver),
      truck2: toInt(formData.truck2),
      driver2: toInt(formData.driver2),
      truck3: toInt(formData.truck3),
      driver3: toInt(formData.driver3),
      scopeOfWork: formData.scopeOfWork,
      crewAssignmentsToAdd: newCrewAssignments,
      crewAssignmentsToDelete: normalizedDeletionIds,
      date: moment(formData["date"]).isValid()
        ? moment(formData["date"]).format("YYYY-MM-DD")
        : null,
      startTime: moment(formData["startTime"]).isValid()
        ? moment(formData["startTime"]).format("HH:mm:ss")
        : null,
      endTime: moment(formData["endTime"]).isValid()
        ? moment(formData["endTime"]).format("HH:mm:ss")
        : null,
      vehicleOption, // ADD vehicleOption TO SUBMIT DATA
    };

    const idInt = Number.parseInt(id, 10);

    // open confirm dialog if deletions exist
    if (normalizedDeletionIds.length > 0) {
      setPendingSubmit({ submitData, idInt, count: normalizedDeletionIds.length });
      setConfirmOpen(true);
      return;
    }

    doSubmit(submitData, idInt);
  };

  // first load
  if (visible && isFetchingObject) {
    const idInt = Number.parseInt(id, 10);
    apolloClient
      .query({
        query: GET_JOB_DAY_BY_ID_FOR_EDIT_FORM,
        variables: { id: idInt },
        fetchPolicy: "network-only",
      })
      .then((result) => {
        if (result?.data?.jobDay) {
          setJobDay(setupJobDayPrefillData(result.data.jobDay));
          setIsFetchingObject(false);
        } else {
          setErrorText("Failed to load job day.");
          setErrorOpen(true);
          setIsFetchingObject(false);
        }
      })
      .catch((e) => {
        console.error(e);
        setErrorText(extractApolloError(e) || "Failed to load job day.");
        setErrorOpen(true);
        setIsFetchingObject(false);
      });

    return null;
  }

  if (visible && !isFetchingObject && jobDay) {
    const editVariants = [{ name: "Edit Job Day", component: <EditJobDay />, stages: 1 }];

    return (
      <>
        <SideViewModal
          show={visible}
          submitText={isSubmitting ? "Saving…" : "Save Changes"}
          submitDisabled={isSubmitting}
          disableSubmit={isSubmitting}
          isSubmitting={isSubmitting}
          onClose={() => {
            setErrors({});
            closeForm();
          }}
          onSubmit={handleSubmit}
          title="Edit Job Day"
          variants={editVariants}
          prefillData={jobDay}
          errors={errors}
        />

        {/* Error dialog */}
        <Dialog open={errorOpen} onClose={() => setErrorOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Couldn't save changes</DialogTitle>
          <DialogContent dividers>
            <Alert severity="error">{errorText}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm dialog for deletions */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning">
              You are about to remove {pendingSubmit?.count} crew assignment
              {pendingSubmit?.count > 1 ? "s" : ""}. This cannot be undone. Continue?
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                setIsSubmitting(false);
                submittingRef.current = false;
                setPendingSubmit(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (pendingSubmit) {
                  doSubmit(pendingSubmit.submitData, pendingSubmit.idInt);
                }
                setConfirmOpen(false);
              }}
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
}
